import { useState, useEffect } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { Users, Settings } from 'lucide-react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(date) {
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(date) {
  return date.toLocaleDateString([], {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

// ---------------------------------------------------------------------------
// KioskIdleScreen
// ---------------------------------------------------------------------------
// The home / idle screen displayed when nobody is interacting with the kiosk.
//
// Props:
//   siteName      (string)   - name of the site
//   onSiteCount   (number)   - people currently on site
//   goToIdentify       (function) - called when the user taps to begin
//   onShowBoard   (function) - called when the user taps "who's on site"
// ---------------------------------------------------------------------------

export default function KioskIdleScreen({
  deviceInfo,
  goToIdentify,
  getOnSiteUsers,
  goToAdmin,
}) {
  const siteName = deviceInfo?.location_name || 'Site';
  const [onSiteCount, setOnSiteCount] = useState(0);

  // -------------------------------------------------------------------------
  // Fetch on-site count on mount and every 60 seconds
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!getOnSiteUsers) return;

    let cancelled = false;

    async function fetchCount() {
      const users = await getOnSiteUsers();
      if (!cancelled) setOnSiteCount(Array.isArray(users) ? users.length : 0);
    }

    fetchCount();
    const id = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [getOnSiteUsers]);

  // -------------------------------------------------------------------------
  // Live clock state -- ticks every second
  // -------------------------------------------------------------------------
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <motion.div
      className="flex-1 flex flex-col items-center justify-center px-6 cursor-pointer"
      onClick={goToIdentify}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') goToIdentify?.();
      }}
      aria-label="Tap to clock in or out"
    >
      {/* ----- Clock ----- */}
      <motion.span
        className="text-7xl font-bold text-slate-800 tracking-tight tabular-nums leading-none"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        aria-live="polite"
      >
        {formatTime(now)}
      </motion.span>

      {/* ----- Date ----- */}
      <motion.p
        className="mt-2 text-xl text-slate-500 font-medium"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15, duration: 0.5 }}
      >
        {formatDate(now)}
      </motion.p>

      {/* ----- Site name with decorative lines ----- */}
      <motion.div
        className="mt-8 flex items-center gap-4"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.25, duration: 0.5 }}
      >
        <span className="block h-px w-12 bg-slate-300" />
        <span className="text-xl font-semibold text-slate-700 tracking-wide">
          {siteName}
        </span>
        <span className="block h-px w-12 bg-slate-300" />
      </motion.div>

      {/* ----- CTA button with breathing pulse ----- */}
      <motion.button
        className="mt-12 px-14 py-6 rounded-2xl bg-slate-900 text-white text-2xl font-semibold shadow-lg"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{
          opacity: 1,
          scale: 1,
          boxShadow: [
            '0 4px 24px rgba(15, 23, 42, 0.15)',
            '0 8px 40px rgba(15, 23, 42, 0.3)',
            '0 4px 24px rgba(15, 23, 42, 0.15)',
          ],
        }}
        transition={{
          opacity: { duration: 0.5, delay: 0.35 },
          scale: { duration: 0.5, delay: 0.35 },
          boxShadow: {
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut',
          },
        }}
        whileTap={{ scale: 0.95 }}
        onClick={(e) => {
          e.stopPropagation();
          goToIdentify?.();
        }}
        aria-label="Tap to clock in or out"
      >
        Tap to Clock In / Out
      </motion.button>

      {/* ----- On-site count (tappable, does NOT trigger goToIdentify) ----- */}
      <motion.button
        className="mt-10 flex items-center gap-3 px-6 py-3 rounded-full bg-white/70 backdrop-blur-sm border border-slate-200 text-lg text-slate-600 font-medium shadow-sm hover:bg-white transition-colors"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        whileTap={{ scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        aria-label={`${onSiteCount} people currently on site. Tap to view.`}
      >
        <Users className="w-6 h-6 text-slate-500" />
        <span>
          <span className="font-bold text-slate-800">{onSiteCount}</span>{' '}
          {onSiteCount === 1 ? 'person' : 'people'} currently on site
        </span>
      </motion.button>

      {/* ----- Admin access button (bottom-right) ----- */}
      {goToAdmin && (
        <motion.button
          className="absolute bottom-6 right-6 p-3 rounded-full text-slate-400 hover:text-slate-700 hover:bg-white/80 bg-white/50 border border-slate-200 shadow-sm transition-colors"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          whileTap={{ scale: 0.9 }}
          onClick={(e) => {
            e.stopPropagation();
            goToAdmin();
          }}
          aria-label="Admin settings"
        >
          <Settings className="w-6 h-6" />
        </motion.button>
      )}
    </motion.div>
  );
}
